import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { useMutation } from "@apollo/client";
import { useRecoilValue } from "recoil";
import { Flex } from "rebass/styled-components";
import { MdBolt, MdOutlineMovieCreation, MdStar } from "react-icons/md";

import { TMoment } from "MVVM/Views/MatchHistory/@types";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { ESupportedGames } from "MVVM/@types";
import { MOMENT_CREATE_CLIP } from "MVVM/GraphQL/mutations/momentRequestClip";
import { ClipBadges } from "MVVM/Components/ClipBadge";
import { TinyAvatar } from "MVVM/Views/MatchHistory/styledComponents";
import { StyledMomentIcon } from "MVVM/Views/MatchHistory/Components/Shared/Match/styledComponents";
import { useUser } from "MVVM/Hooks/useUser";
import { Modal } from "MVVM/Components/Modal";
import { QuickEdit } from "MVVM/Components/QuickEdit";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import {
  QuickEditPreferredLoadout,
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
  QuickEditPreferredPresetId,
} from "MVVM/State/quickEdit";
import { addPendingClipCreation } from "MVVM/Utilities/ClipLimits/pendingClipCreations";
import { analytics } from "MVVM/Analytics";
import { momentRound } from "./ViewModel";

type TState = "idle" | "creating" | "pending" | "error";

const MAX_VICTIMS = 4;
const HIDE_WATERMARK_CARD = "60a6d86a8c4fd555132afc97";

type TClipPrefs = {
  loadout?: { _id?: string } | null;
  finalTouches: string[];
  thumbnail?: string | null;
  presetId?: string | null;
};

const buildEffectsOverrides = ({
  loadout,
  finalTouches,
  thumbnail,
  presetId,
}: TClipPrefs) => ({
  thumbnail: thumbnail || undefined,
  loadout: loadout?._id || "NO_LOADOUT",
  rapid: false,
  contextCameras: finalTouches.includes("contextCams"),
  applySignatureMusic: finalTouches.includes("music"),
  applySignatureCards: finalTouches.includes("signature"),
  disableJumpCuts: finalTouches.includes("disableJumpCuts"),
  applyMountedCameras: finalTouches.includes("mountedCameras"),
  applyFinalKillCamera: finalTouches.includes("finalKillCamera"),
  creatorCards: [
    ...(presetId ? [presetId] : []),
    ...(finalTouches.includes("hideWatermark") ? [HIDE_WATERMARK_CARD] : []),
  ],
});

export const MomentRow = ({
  moment,
  matchId,
  active,
  autoScroll,
  isBest,
  onJump,
}: {
  moment: TMoment;
  matchId?: string;
  active: boolean;
  autoScroll?: boolean;
  isBest?: boolean;
  onJump: () => void;
}) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const { allstarUser } = useUser();
  const [state, setState] = useState<TState>("idle");

  const preferredLoadout = useRecoilValue(QuickEditPreferredLoadout);
  const preferredFinalTouches = useRecoilValue(QuickEditPreferredFinalTouches);
  const preferredThumbnail = useRecoilValue(QuickEditPreferredThumbnail);
  const preferredPresetId = useRecoilValue(QuickEditPreferredPresetId);

  const [momentCreateClip] = useMutation(MOMENT_CREATE_CLIP);
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [eligibilityOpen, setEligibilityOpen] = useState(false);

  useEffect(() => {
    if (active && autoScroll)
      rowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [active, autoScroll]);

  const round = momentRound(moment);
  const filteredTags = moment.clipTags?.filter(
    (t) => t.key !== "CS_Round" && t.key !== "CS_Weapons",
  );
  const victims = moment.victimData || [];
  const shownVictims = victims.slice(0, MAX_VICTIMS);
  const extraVictims = victims.length - shownVictims.length;
  const killIcon = moment.icons?.[0];

  const handleOneClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!allstarUser.loggedIn || state === "creating" || state === "pending")
      return;
    analytics.track("Match VOD - 1-Click Clip", {
      matchId,
      momentId: moment.id,
    });
    setState("creating");
    try {
      const res = await momentCreateClip({
        variables: {
          momentCreateClipInput: {
            ids: [moment.id],
            matchId,
            effectsOverrides: buildEffectsOverrides({
              loadout: preferredLoadout,
              finalTouches: preferredFinalTouches,
              thumbnail: preferredThumbnail,
              presetId: preferredPresetId,
            }),
            checkEligibility: true,
          },
        },
      });
      const errors = res?.data?.momentCreateClip?.errors;
      if (errors?.find((er: { field: string }) => er.field === "eligibility")) {
        setState("idle");
        setEligibilityOpen(true);
        return;
      }
      addPendingClipCreation();
      setState("pending");
    } catch {
      setState("error");
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!allstarUser.loggedIn) return;
    analytics.track("Match VOD - Edit & Clip", {
      matchId,
      momentId: moment.id,
    });
    setQuickEditOpen(true);
  };

  return (
    <>
      {quickEditOpen && (
        <Modal isModalOpen setModalOpen={setQuickEditOpen}>
          <QuickEdit
            momentId={moment.id}
            closeModal={() => setQuickEditOpen(false)}
            onCreate={() => setState("pending")}
          />
        </Modal>
      )}
      {eligibilityOpen && (
        <Modal isModalOpen setModalOpen={setEligibilityOpen}>
          <ClipEligibilityModal
            surface="Match VOD"
            clipQuota={allstarUser?.user?.clipQuota}
            userGroup={allstarUser?.user?.userGroup?.type}
            isCs2Clip={
              getMatchHistoryGameEnum(moment.game) === ESupportedGames.CS2
            }
            onClose={() => setEligibilityOpen(false)}
          />
        </Modal>
      )}

      <Card ref={rowRef} $active={active} onClick={onJump}>
        <Head>
          <Players>
            {moment.player?.avatar && <TinyAvatar src={moment.player.avatar} />}
            {victims.length > 0 && (
              <>
                {killIcon && <KillIcon src={killIcon} />}
                <Victims>
                  {shownVictims.map((v, i) => (
                    <TinyAvatar
                      key={`${moment.id}-${v.gameId}-${i}`}
                      src={v.avatar}
                    />
                  ))}
                </Victims>
                {extraVictims > 0 && <VictimMore>+{extraVictims}</VictimMore>}
              </>
            )}
          </Players>
          <Title>{moment.title}</Title>
          {isBest && (
            <BestStar title="Your best moment from this match">
              <MdStar size={16} />
            </BestStar>
          )}
          {round != null && <RoundChip>R{round}</RoundChip>}
        </Head>

        {filteredTags && filteredTags.length > 0 && (
          <Tags>
            <ClipBadges tags={filteredTags} size="sm" maxVisible={3} />
          </Tags>
        )}

        <Actions>
          <OneClickBtn
            onClick={handleOneClick}
            disabled={state === "creating" || state === "pending"}
            $pending={state === "pending"}
          >
            <MdBolt size={15} />
            {state === "creating"
              ? "Creating..."
              : state === "pending"
              ? "Clip Pending"
              : state === "error"
              ? "Retry"
              : "1-Click Clip"}
          </OneClickBtn>
          <EditBtn onClick={handleEdit}>
            <MdOutlineMovieCreation size={15} />
            Edit &amp; Clip
          </EditBtn>
        </Actions>
      </Card>
    </>
  );
};

const Card = styled.div<{ $active?: boolean }>`
  background: ${({ theme }) => theme.colors.ned};
  border-bottom: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 9px 12px;
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.navy};
  }

  ${({ $active }) =>
    $active &&
    css`
      background: rgba(77, 189, 148, 0.16);

      &:hover {
        background: rgba(77, 189, 148, 0.24);
      }
    `}
`;

const Head = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
`;

const BestStar = styled.span`
  align-items: center;
  color: ${({ theme }) => theme.colors.bestMoment};
  display: inline-flex;
  flex-shrink: 0;
`;

const Players = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 5px;
`;

// Pin the kill icon; StyledMomentIcon's flex: 1 1 28px makes it grow here.
const KillIcon = styled(StyledMomentIcon)`
  flex: 0 0 auto;
  height: 16px;
  width: auto;
`;

const Victims = styled(Flex)`
  align-items: center;
  flex-direction: row;

  & > * {
    margin-left: -6px;
  }
  & > *:first-child {
    margin-left: 0;
  }
`;

const VictimMore = styled.span`
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
`;

const RoundChip = styled.span`
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 5px;
  color: ${({ theme }) => theme.colors.chalk};
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
`;

const Title = styled.span`
  color: ${({ theme }) => theme.colors.ice};
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
`;

const baseBtn = `
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  flex: 1;
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  gap: 5px;
  justify-content: center;
  padding: 7px 6px;
  transition: opacity 0.12s ease, background 0.12s ease;

  & svg {
    flex-shrink: 0;
  }
`;

const OneClickBtn = styled.button<{ $pending?: boolean }>`
  ${baseBtn}
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.chalk};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
  }

  &:disabled {
    cursor: default;
    ${({ $pending, theme }) =>
      $pending &&
      css`
        color: ${theme.colors.envy};
        border-color: ${theme.colors.envy};
      `}
  }
`;

const EditBtn = styled.button`
  ${baseBtn}
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    opacity: 0.9;
  }
`;
